import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import Icon from "../components/Icon";
import { Avatar } from "../components/Atoms";
import { puedeTocarAsistencia, esGestor, esSuper, equiposDe, diasDelPeriodo, esFinde,
         lunesDe, sumarDias, numeroSemana } from "../utils";
import { PALETA_PERSONAS } from "../constants";
import * as api from "../api";
import HorasSemanales from "../components/widgets/HorasSemanales";
import ResumenEquipo, { horasReales } from "../components/widgets/ResumenEquipo";
import { horasDeTramo } from "../horasTramo";
import { AvatarOdoo } from "../components/widgets/SelectorUsuario";
import { clicable } from "../components/ClicableTeclado.jsx";
import FicharAhora from "../components/widgets/FicharAhora";
import { useJornada } from "../hooks/useJornada";
import { hhmm } from "../utils/hora";

// Registro de fichajes hechos desde ALM, en calendario mensual.
//
// Entra todo el mundo, pero no todos ven lo mismo: el backend recorta las filas
// en SQL (ambitoAsistencias en backend/lib/permisos.js). Un usuario normal solo
// recibe las suyas; gestor y admin reciben la plantilla entera.
//
// Corregir el registro es otra cosa: solo el admin, y solo sobre su equipo,
// salvo que este en TIC. Aqui se esconde el boton al que no puede; el servidor
// lo vuelve a comprobar.
//
// Con la plantilla entera delante no se lee nada, asi que el filtro por equipos
// deja el mes de los departamentos que interesan. Es solo un filtro de
// pantalla, los datos que llegan son los mismos.
//
// Y a quien manda no se le deja marcar la plantilla completa: con los diecisiete
// equipos encendidos las barras son un promedio de la empresa que no dice nada
// de nadie. La pantalla existe para comparar departamentos, asi que se pide
// dejar al menos uno fuera; la propia gente de uno se ve marcando solo el suyo.
//
// El periodo se manda desde un solo sitio. Antes el panel de "Mi jornada" tenia
// su mes y sus flechas, y el calendario los suyos: se podia acabar leyendo
// agosto arriba y septiembre abajo creyendo que era el mismo mes. Ahora el mes y
// la semana viven aqui, en la barra de controles de arriba, y los dos bloques
// los reciben; el calendario ademas remarca la semana que se esta mirando
// arriba, y cada fila del mes es un boton para elegirla.

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
               "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS = ["L","M","X","J","V","S","D"];

// Quien no está en ningún equipo también ficha: sin esta casilla, filtrar por
// equipos lo dejaría fuera de la vista sin que se note.
const SIN_EQUIPO = "__sin__";

// Radio del punto que da el navegador. Hasta 100 m es GPS: sirve para saber
// desde donde se ficho. Por encima de 1 km ya no es un sitio, es la ciudad: eso
// es lo que devuelve un PC sin GPS, que se localiza por wifi o por IP.
const colorPrecision = m => (m <= 100 ? "#22c55e" : m <= 1000 ? "#f59e0b" : "var(--red)");
const precisionTexto = m => !m ? "Sin dato de precisión"
  : m <= 100  ? `Punto de GPS, con ${m} m de margen`
  : m <= 1000 ? `Punto aproximado, con ${m} m de margen`
  : `Solo aproximado: ${(m / 1000).toFixed(1)} km de margen. Suele ser un equipo sin GPS, localizado por wifi o por IP.`;

// Cuando Odoo no logra situar la IP rellena la ciudad y el pais con
// "Desconocido" en vez de dejarlos vacios. Enseñar "Desconocido, Desconocido"
// en cada linea no dice nada: se trata como si no hubiera dato.
const NO_SABE = /^(desconocido|unknown|n\/?a|-)$/i;
const sitio = f => [f.odoo_ciudad, f.odoo_pais]
  .filter(v => v && !NO_SABE.test(String(v).trim()))
  .join(", ");

const fmtHoras = h => {
  if (h === null || h === undefined) return "—";
  const m = Math.round(h * 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

// Primer dia laborable del mes. Con un mes que empieza en sabado, el dia 1 no
// abre nada: la semana de trabajo arranca el lunes siguiente.
function primerLaborable(anio, mes) {
  const mm = String(mes + 1).padStart(2, "0");
  const ultimo = new Date(anio, mes + 1, 0).getDate();
  for (let d = 1; d <= ultimo; d++) {
    const iso = `${anio}-${mm}-${String(d).padStart(2, "0")}`;
    if (!esFinde(iso)) return iso;
  }
  return `${anio}-${mm}-01`;      // un mes entero de fin de semana no existe
}

// Rejilla del mes empezando en lunes, con huecos al principio.
function rejilla(anio, mes) {
  const primero = new Date(anio, mes, 1);
  const hueco = (primero.getDay() + 6) % 7;            // lunes = 0
  const dias = new Date(anio, mes + 1, 0).getDate();
  const celdas = Array(hueco).fill(null);
  for (let d = 1; d <= dias; d++) {
    celdas.push(`${anio}-${String(mes + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  return celdas;
}

// Desplegable propio (Equipos, Personas): Escape lo cierra y devuelve el foco al boton, y al
// abrirlo el foco entra en el panel para que el teclado siga ahi. Devuelve los refs.
function useDesplegable(abierto, cerrar) {
  const boton = useRef(null);
  const panel = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    panel.current?.querySelector("button, input")?.focus();
    const tecla = e => { if (e.key === "Escape") { cerrar(); boton.current?.focus(); } };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [abierto, cerrar]);
  return { boton, panel };
}

export default function AsistenciasView({ showToast, users = [], teams: equiposTodos = [], currentUser,
                                          festivos = {}, vacaciones = [], fichaje = null,
                                          diaInicial = null, alUsarDiaInicial = null }) {
  // Quien no coordina solo ve sus propios fichajes (el servidor lo recorta: ambitoAsistencias),
  // asi que elegir equipos o personas no le sirve de nada. Se le esconde en vez de dejarle una
  // barra de controles que no cambian lo que ve.
  const verOtros = esGestor(currentUser);
  // Lote S6: el gestor y el admin solo reciben a su gente (ambitoAsistencias), asi que solo se les ofrecen SUS equipos; el super
  // y quien solo se ve a si mismo, todos (a este ultimo se le marcan todos, ver equiposOn).
  const teams = useMemo(() => (verOtros && !esSuper(currentUser) ? equiposDe(currentUser, equiposTodos) : equiposTodos),
    [verOtros, currentUser, equiposTodos]);
  const hoy = new Date();
  // Un aviso de la campana (fichaje corregido en Odoo) abre la vista ya en ese dia.
  const [anio, setAnio] = useState(() => diaInicial ? Number(diaInicial.slice(0, 4)) : hoy.getFullYear());
  const [mes,  setMes]  = useState(() => diaInicial ? Number(diaInicial.slice(5, 7)) - 1 : hoy.getMonth());
  // La semana concreta que mira "Mi jornada" y que el calendario remarca. Es
  // siempre una semana: no marcar ninguna dejaba el panel de arriba y la
  // rejilla de abajo sin nada que los relacionase.
  const isoDeHoy = new Intl.DateTimeFormat("sv-SE", { timeZone:"Europe/Madrid" }).format(new Date());
  const [semana, setSemana] = useState(() => lunesDe(diaInicial || isoDeHoy));
  const [todas, setTodas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [diaAbierto, setDiaAbierto] = useState(diaInicial);
  const [cargada, setCargada] = useState(false);       // la primera carga ya ha terminado
  const diaPendiente = useRef(null);                  // dia a abrir cuando llegue el mes que falta
  const [sincronizando, setSincronizando] = useState(false);
  // Dias que se piden a Odoo al sincronizar a mano. El automatico de la apertura
  // solo mira 60 dias, que es donde caen las correcciones recientes; para
  // rellenar el historico hay que poder pedir mas.
  const [dias, setDias] = useState(60);

  const cargarMes = useCallback(() => {
    const ultimo = new Date(anio, mes + 1, 0).getDate();
    const mm = String(mes + 1).padStart(2, "0");
    setCargando(true);
    return api.getAsistencias(`${anio}-${mm}-01`, `${anio}-${mm}-${ultimo}`)
      .then(r => setTodas(r))
      .catch(e => showToast(e.message || "No se pudo cargar el registro", "error"))
      .finally(() => {
        setCargando(false);
        setCargada(true);
        if (diaPendiente.current) { setDiaAbierto(diaPendiente.current); diaPendiente.current = null; }
      });
  }, [anio, mes]); // eslint-disable-line

  useEffect(() => { cargarMes(); }, [cargarMes]);

  // El aviso llega con la vista ya abierta: se va a su mes y se abre el dia al terminar de cargarlo.
  useEffect(() => {
    if (!diaInicial) return;
    if (Number(diaInicial.slice(0, 4)) === anio && Number(diaInicial.slice(5, 7)) - 1 === mes) setDiaAbierto(diaInicial);
    else {
      diaPendiente.current = diaInicial;
      setAnio(Number(diaInicial.slice(0, 4)));
      setMes(Number(diaInicial.slice(5, 7)) - 1);
    }
    setSemana(lunesDe(diaInicial));
    if (alUsarDiaInicial) alUsarDiaInicial();
  }, [diaInicial]); // eslint-disable-line

  // Contrastar con Odoo a mano. Al abrir ALM esto ya se intenta solo, pero una
  // vez por pestaña y sin decir nada si falla: quien mira el registro y lo ve
  // vacio necesita poder lanzarlo y leer que ha pasado.
  //
  // Trae SOLO los fichajes de quien pulsa: se consulta con su sesion de Odoo.
  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.sincronizarFichajes(dias);
      if (r.importados > 0) {
        showToast(`${r.importados} fichaje${r.importados === 1 ? "" : "s"} traído${r.importados === 1 ? "" : "s"} de Odoo`
                  + (r.corregidos ? ` y ${r.corregidos} con la hora al día` : ""));
        await cargarMes();
      } else if (r.corregidos) {
        // Sin nada que importar puede haber horas que hayan cambiado en Odoo: es
        // lo que pasa cuando RRHH aprueba una modificacion de asistencia.
        showToast(`${r.corregidos} fichaje${r.corregidos === 1 ? "" : "s"} con la hora corregida en Odoo`);
        await cargarMes();
      } else {
        showToast(`Odoo no tiene nada que falte aquí: ${r.revisados} fichaje`
                  + `${r.revisados === 1 ? "" : "s"} revisado${r.revisados === 1 ? "" : "s"} `
                  + `desde ${r.desde}`, "warn");
      }
    } catch (e) {
      showToast(e.message || "No se pudo contrastar con Odoo", "error");
    } finally {
      setSincronizando(false);
    }
  };

  // ── Equipos ────────────────────────────────────────────────────────────────

  const miembrosDe = useMemo(() => {
    const m = {};
    teams.forEach(t => { m[String(t.id)] = new Set((t.members || []).map(String)); });
    return m;
  }, [teams]);

  const sinEquipo = useMemo(() => {
    const conEquipo = new Set();
    // Con TODOS los equipos: quien esta en un equipo que no se ofrece no es «sin equipo»
    equiposTodos.forEach(t => (t.members || []).forEach(u => conEquipo.add(String(u))));
    return new Set(users.filter(u => !conEquipo.has(String(u.id))).map(u => String(u.id)));
  }, [users, equiposTodos]);

  // Quien manda entra viendo SU departamento y nadie mas. Al super el backend le
  // sirve la plantilla entera —diecisiete equipos y cientos de fichajes— y de
  // entrada eso no se lee: lo que casi siempre viene a mirar es su propia gente, y
  // el resto esta a un clic en el desplegable. Admin y gestor solo reciben a su gente.
  //
  // A quien no manda se le marca todo, que es lo mismo que marcar lo suyo: el
  // servidor solo le manda sus propios fichajes. Asi, si algun dia se le amplia
  // el ambito, no se encuentra la pantalla filtrada sin haber pedido nada.
  //
  // Sin equipo propio, a quien manda se le marca el equipo mas grande y no todos:
  // la plantilla entera de golpe es justo lo que esta pantalla ya no permite.
  const [equiposOn, setEquiposOn] = useState(null);   // null = todavía sin fijar
  useEffect(() => {
    if (equiposOn || !teams.length) return;
    if (!esGestor(currentUser)) {
      setEquiposOn(new Set([...teams.map(t => String(t.id)), SIN_EQUIPO]));
      return;
    }
    const mios = equiposDe(currentUser, teams).map(t => String(t.id));
    if (mios.length) { setEquiposOn(new Set(mios)); return; }
    const mayor = [...teams].sort((a, b) => (b.members || []).length - (a.members || []).length)[0];
    setEquiposOn(new Set([String(mayor.id)]));
  }, [teams, equiposOn, currentUser]);

  const [abiertoSel, setAbiertoSel] = useState(false);
  const cerrarSel = useCallback(() => setAbiertoSel(false), []);
  const desplSel = useDesplegable(abiertoSel, cerrarSel);

  // Grupos que se pueden marcar: los equipos y, si hay gente suelta, "sin equipo".
  const grupos = useMemo(
    () => [...teams.map(t => String(t.id)), ...(sinEquipo.size ? [SIN_EQUIPO] : [])],
    [teams, sinEquipo]);

  // A gestor y admin se les pide elegir: al menos un grupo y nunca todos. Con
  // todos marcados esto vuelve a ser el promedio de la empresa, que es lo que no
  // se podia leer; dejando uno fuera, cada barra es un departamento concreto.
  //
  // A quien solo ve sus propios fichajes no se le aplica: para el, "todos" y "el
  // mio" son la misma lista, y bloquearselo solo seria un boton que no responde.
  const soloComparar = esGestor(currentUser) && grupos.length > 1;
  // Con solo dos grupos, marcar los dos ES la comparación: ahí la regla de
  // "nunca todos" dejaría sin comparar, que es lo contrario de lo que busca.
  const niTodos = esGestor(currentUser) && grupos.length > 2;
  const bloqueoDe = key => {
    if (!soloComparar || !equiposOn) return null;
    if (equiposOn.has(key)) {
      return equiposOn.size <= 1
        ? "Deja al menos un equipo marcado: sin ninguno el calendario sale vacío"
        : null;
    }
    return niTodos && equiposOn.size >= grupos.length - 1
      ? "No se puede ver la plantilla entera de una vez: deja al menos un equipo "
        + "fuera para que la comparación diga algo de alguien"
      : null;
  };

  const toggleEquipo = key => {
    if (bloqueoDe(key)) return;
    setEquiposOn(p => {
      const n = new Set(p || []);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // Sin equipos cargados aún no se filtra nada: mejor el mes completo que un
  // calendario vacío mientras llega la lista.
  const equipoDe = uid => {
    const k = String(uid);
    for (const t of teams) if (miembrosDe[String(t.id)]?.has(k)) return String(t.id);
    return SIN_EQUIPO;
  };

  // Etiqueta del equipo de cada persona: la usan el detalle del dia, el resumen
  // de abajo y la lista de gente con la que se compara.
  const nombreEquipo = uid => {
    const k = equipoDe(uid);
    if (k === SIN_EQUIPO) return null;
    const t = teams.find(t => String(t.id) === k);
    return t ? { name: t.name, color: t.color || "var(--text3)" } : null;
  };

  const visible = uid => {
    if (!equiposOn) return true;
    const k = String(uid);
    // Alguien puede estar en varios equipos: basta con que uno esté marcado.
    for (const t of teams) if (miembrosDe[String(t.id)]?.has(k) && equiposOn.has(String(t.id))) return true;
    return sinEquipo.has(k) ? equiposOn.has(SIN_EQUIPO) : false;
  };

  const filas = useMemo(() => todas.filter(f => visible(f.user_id)),
    [todas, equiposOn, miembrosDe, sinEquipo]); // eslint-disable-line

  const nEquipos = teams.length + (sinEquipo.size ? 1 : 0);
  const nMarcados = equiposOn
    ? [...equiposOn].filter(k => k !== SIN_EQUIPO || sinEquipo.size).length
    : nEquipos;
  const todosLosEquipos = () => setEquiposOn(new Set([...teams.map(t => String(t.id)), SIN_EQUIPO]));
  const ningunEquipo    = () => setEquiposOn(new Set());
  // El atajo que si tiene sentido cuando no se puede marcar todo: volver a lo mio.
  const misEquipos = equiposDe(currentUser, teams).map(t => String(t.id));
  const soloLosMios = () => setEquiposOn(new Set(misEquipos));

  // El backend ya devuelve el día natural de Madrid calculado: agruparlo aquí
  // con la fecha del navegador reabriría los desfases de huso.
  const porDia = useMemo(() => {
    const m = {};
    filas.forEach(f => { (m[f.dia] = m[f.dia] || []).push(f); });
    return m;
  }, [filas]);

  // ── Con quien se compara mi jornada ───────────────────────────────────────
  //
  // Mis horas solas no dicen si un dia flojo fue mio o de todo el
  // departamento. Arriba, al lado de mi barra, va UNA BARRA POR PERSONA de la
  // gente marcada, sacada de los fichajes que ya estan en pantalla: los mismos
  // que pinta el calendario, ni una fila mas.

  // Horas de cada persona en cada dia del mes, contando cada tramo una vez.
  const horasPorPersonaDia = useMemo(() => {
    const porClave = {};
    filas.forEach(f => {
      const k = `${f.dia}|${f.user_id}`;
      (porClave[k] = porClave[k] || []).push(f);
    });
    const m = {};
    for (const [k, lista] of Object.entries(porClave)) {
      const h = horasReales(lista);
      if (!(h > 0)) continue;
      const [dia, uid] = k.split("|");
      (m[uid] = m[uid] || {})[dia] = h;
    }
    return m;
  }, [filas]);

  // Las ausencias de cada persona, dia a dia. Solo las APROBADAS: un borrador o
  // una peticion rechazada no eximen a nadie de la jornada, y pintarlas como
  // ausencia daria por bueno lo que RRHH aun no ha firmado —o ha denegado—.
  const ausenciasPorPersonaDia = useMemo(() => {
    const mm = `${anio}-${String(mes + 1).padStart(2, "0")}`;
    const m = {};
    vacaciones.forEach(v => {
      if (v.estado && v.estado !== "aprobada") return;
      const uid = String(v.user_id);
      diasDelPeriodo(v.fecha, v.fecha_fin).forEach(d => {
        if (!d.startsWith(mm)) return;
        (m[uid] = m[uid] || {})[d] = v.tipo || "vacaciones";
      });
    });
    return m;
  }, [vacaciones, anio, mes]);

  // La ficha de empleado de Odoo de cada uno, que es la clave de su foto. Viene
  // en el propio registro: es con la que ALM ficha en Odoo.
  const empleadoDe = useMemo(() => {
    const m = {};
    todas.forEach(f => {
      const uid = String(f.user_id);
      if (f.odoo_employee_id && !m[uid]) m[uid] = Number(f.odoo_employee_id);
    });
    return m;
  }, [todas]);

  // Quien puede entrar en la comparación: la gente de los equipos marcados que
  // tiene algo que enseñar este mes —un fichaje o una ausencia aprobada—.
  //
  // Quien no tiene ninguna de las dos cosas NO sale. Es la misma regla del
  // resumen de abajo: el registro no sabe si estaba de baja o si nadie fichó
  // por él, y plantarle una barra a cero al lado de las demás sería acusarle de
  // algo que no consta.
  //
  // Yo tampoco salgo aquí: mi barra ya va primera, y con mis horas de Odoo, que
  // es otra fuente. Duplicarme daría dos cifras distintas de la misma persona.
  const gente = useMemo(() => {
    const uids = new Set([...Object.keys(horasPorPersonaDia),
                          ...Object.keys(ausenciasPorPersonaDia)]);
    const yo = String(currentUser?.id);
    return [...uids]
      .filter(uid => uid !== yo && visible(uid)
                     // Una ausencia sin un solo fichaje en el mes no basta para
                     // entrar: seria una barra rayada de alguien que quiza ni
                     // trabaja ya aqui.
                     && (horasPorPersonaDia[uid]
                         || Object.keys(ausenciasPorPersonaDia[uid] || {}).length > 0))
      .map(uid => ({
        uid,
        nombre: users.find(u => String(u.id) === uid)?.name
                || todas.find(f => String(f.user_id) === uid)?.user_name || "—",
        equipo: nombreEquipo(uid)?.name || null,
        empId: empleadoDe[uid] || null,
        horasDia: horasPorPersonaDia[uid] || {},
        ausenciaDia: ausenciasPorPersonaDia[uid] || {},
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))
      // El color se reparte por posicion en la lista completa, no en la de
      // marcados: asi apagar a alguien no le cambia el color a los demas.
      .map((p, i) => ({ ...p, color: PALETA_PERSONAS[i % PALETA_PERSONAS.length] }));
  }, [horasPorPersonaDia, ausenciasPorPersonaDia, empleadoDe, users, todas,
      currentUser, equiposOn, miembrosDe, sinEquipo]); // eslint-disable-line

  // La jornada de cada persona que sale en pantalla (la mia, las barras y la tabla del mes),
  // en un solo viaje: el objetivo diario sale de aqui y no de un 8 fijo.
  const idsJornada = useMemo(
    () => [currentUser?.id, ...filas.map(f => f.user_id)].filter(Boolean).map(Number),
    [currentUser, filas]);
  const { jornadaDe } = useJornada(idsJornada, currentUser?.id);

  // A quien se ha apagado a mano. Se guarda al reves —los apagados— para que al
  // cambiar de equipo la gente nueva entre marcada sin tener que tocar nada.
  const [personasOff, setPersonasOff] = useState(() => new Set());
  const [abiertoPers, setAbiertoPers] = useState(false);
  const cerrarPers = useCallback(() => setAbiertoPers(false), []);
  const desplPers = useDesplegable(abiertoPers, cerrarPers);
  const genteOn = gente.filter(p => !personasOff.has(p.uid));
  const togglePersona = uid => setPersonasOff(p => {
    const n = new Set(p);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    return n;
  });

  // Las fotos, en UN solo viaje y para toda la gente de la lista. La clave del
  // efecto es la lista de fichas: con el array se relanzaria en cada render.
  const [, redibujar] = useState(0);
  const clavesFoto = [...new Set([...gente.map(p => p.empId),
                                  empleadoDe[String(currentUser?.id)]])]
    .filter(Boolean).sort((a, b) => a - b).join(",");
  useEffect(() => {
    if (!clavesFoto) return;
    api.precargarAvatares("hr.employee", clavesFoto.split(",").map(Number))
      .then(() => redibujar(n => n + 1));
  }, [clavesFoto]);

  const comparativa = useMemo(() => {
    if (!genteOn.length) return null;
    // La media por persona, que es lo que sigue alimentando la tira de semanas:
    // doce barras por semana en una tira de 26 px no se leen. Se hace sobre
    // quien FICHO ese dia y no sobre la plantilla: dividir entre todos hundiria
    // la referencia cada dia de vacaciones ajenas.
    const suma = {}, cuenta = {};
    genteOn.forEach(p => Object.entries(p.horasDia).forEach(([dia, h]) => {
      suma[dia] = (suma[dia] || 0) + h;
      cuenta[dia] = (cuenta[dia] || 0) + 1;
    }));
    const mediaDia = {};
    for (const dia of Object.keys(suma)) mediaDia[dia] = suma[dia] / cuenta[dia];

    const marcados = grupos.filter(k => !equiposOn || equiposOn.has(k));
    const etiqueta = marcados.length === 1
      ? (marcados[0] === SIN_EQUIPO ? "Sin equipo"
         : teams.find(t => String(t.id) === marcados[0])?.name || "el equipo")
      : `${marcados.length} equipos`;
    return { etiqueta, personas: genteOn.map(p => ({ ...p, jornada: jornadaDe(p.uid) })), mediaDia };
  }, [genteOn, grupos, equiposOn, teams, jornadaDe]); // eslint-disable-line

  const celdas = rejilla(anio, mes);
  // Las filas del mes, de lunes a domingo, con el lunes al que pertenece cada
  // una: es lo que permite remarcar la semana que se esta mirando arriba y
  // convertir cada fila en un boton para elegirla.
  const semanasRejilla = useMemo(() => {
    const out = [];
    for (let i = 0; i < celdas.length; i += 7) {
      const fila = celdas.slice(i, i + 7);
      const primerDia = fila.find(Boolean);
      // La ultima semana del mes suele acabar a media fila. Se rellena con
      // huecos para que la franja de la semana marcada llegue hasta el domingo
      // en vez de cortarse en el ultimo dia del mes.
      while (fila.length < 7) fila.push(null);
      out.push({ fila, lunes: lunesDe(primerDia) });
    }

    // Un mes que empieza en sabado abre con una fila que no tiene ni un dia
    // laborable SUYO: de lunes a viernes es del mes anterior y solo le quedan el
    // sabado y el domingo. Si encima nadie ficho esos dos dias, la fila entera
    // esta en blanco y lo unico que dice es que el mes de verdad empieza el
    // lunes siguiente. Fuera.
    //
    // Con un fichaje de fin de semana —una guardia— se queda: ahi si hay algo
    // que ver, y esconderlo dejaria fichajes contados arriba y invisibles aqui.
    // Y si hoy cae en esos dias tambien se queda, para que la semana en curso no
    // se quede sin su marca.
    //
    // Solo puede pasarle a la PRIMERA fila: si la ultima tuviera solo sabado y
    // domingo del mes, su lunes tambien seria del mes.
    const dias = out[0]?.fila.filter(Boolean) || [];
    if (dias.length && dias.every(iso => esFinde(iso) && iso !== isoDeHoy
                                         && !(porDia[iso] || []).length)) {
      out.shift();
    }
    return out;
  }, [celdas, porDia, isoDeHoy]); // eslint-disable-line
  const card = { background:"var(--surface)", borderRadius:13, border:"1px solid var(--border)",
                 boxShadow:"var(--shadow-sm)" };

  // Cambiar de mes tiene que llevarse la semana con el: si no, se quedaba
  // marcada una semana de otro mes y el calendario no remarcaba nada.
  const irAlMes = (a, m) => {
    setAnio(a); setMes(m);
    const primero = `${a}-${String(m + 1).padStart(2, "0")}-01`;
    const ultimo  = `${a}-${String(m + 1).padStart(2, "0")}-${String(new Date(a, m + 1, 0).getDate()).padStart(2, "0")}`;
    // La primera semana del mes con la que se puede entrar es la del primer dia
    // LABORABLE: cuando el mes empieza en sabado, la semana del dia 1 es la del
    // mes anterior y el calendario ya no la pinta, asi que marcarla dejaria la
    // rejilla sin ninguna semana remarcada.
    const primeraSem = lunesDe(primerLaborable(a, m));
    setSemana(p => {
      // Si la semana marcada ya toca ese mes, se respeta: al volver de un mes
      // vecino se recupera donde estabas.
      if (p >= primeraSem && p <= ultimo) return p;
      // El mes en curso entra por la semana de hoy; uno cerrado, por la primera.
      return isoDeHoy >= primero && isoDeHoy <= ultimo ? lunesDe(isoDeHoy) : primeraSem;
    });
  };
  const mover = paso => {
    const d = new Date(anio, mes + paso, 1);
    irAlMes(d.getFullYear(), d.getMonth());
  };
  const moverSemana = paso => setSemana(p => sumarDias(p, paso * 7));
  const esMesActual   = anio === hoy.getFullYear() && mes === hoy.getMonth();
  const esSemanaActual = semana === lunesDe(isoDeHoy);
  const mesFuturo = new Date(anio, mes, 1) >= new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const semanaFutura = semana >= lunesDe(isoDeHoy);
  const rangoSemana = `${Number(semana.slice(8))} – ${new Date(`${sumarDias(semana, 6)}T12:00:00`)
    .toLocaleDateString("es-ES", { day:"numeric", month:"short" }).replace(".", "")}`;

  const detalle = diaAbierto ? (porDia[diaAbierto] || []) : [];

  // Un fichaje que nunca llego a Odoo no es jornada de nadie: son los intentos
  // que se quedaron en error. Se pueden descartar del registro, y solo esos: lo
  // que si esta en Odoo se queda, que es lo que vale legalmente.
  const borrarFallido = async fila => {
    if (fila.odoo_estado === "ok") return;
    if (!puedeTocarAsistencia(currentUser, teams, fila.user_id)) return;
    const cuando = `${fila.accion} de ${hhmm(fila.ts_servidor, "—")}`;
    if (!window.confirm(`¿Descartar del registro la ${cuando} de ${fila.user_name}?\n\n`
                        + "Este fichaje no llegó a Odoo. La acción no se puede deshacer.")) return;
    try {
      await api.borrarFichaje(fila.id);
      setTodas(p => p.filter(f => String(f.id) !== String(fila.id)));
      showToast("Fichaje descartado del registro");
    } catch (e) {
      showToast(e.message || "No se pudo descartar el fichaje", "error");
    }
  };

  // Si al cambiar el filtro el dia abierto se queda sin fichajes, la ficha se
  // quedaba en pantalla vacia.
  useEffect(() => {
    // Antes de la primera carga no hay fichajes que contar: no cerrar un dia abierto desde un aviso.
    if (cargada && !cargando && diaAbierto && !detalle.length) setDiaAbierto(null);
  }, [diaAbierto, detalle.length, cargada, cargando]);

  return (
    // La seccion Calendario recorta lo que sobra (overflow:hidden y alto fijo).
    // Esta pestana creció con el resumen del mes y ya no cabe de una vez, asi
    // que se desplaza por dentro: si no, el calendario se salia de su caja y el
    // resumen se pintaba encima.
    <div className="fu col-scroll" style={{ display:"flex", flexDirection:"column", gap:12, minHeight:0, flex:1 }}>

      {/* Barra de mandos del periodo. Es la unica: manda a la vez sobre "Mi
          jornada" y sobre el calendario, que antes tenian cada uno los suyos.
          Va pegada arriba porque es lo primero que se toca y lo primero que hay
          que poder leer para saber que se esta viendo. */}
      <div style={{ ...card, padding:"9px 12px", display:"flex", alignItems:"center", gap:10,
                    flexWrap:"wrap", position:"sticky", top:0, zIndex:20 }}>

        {/* El mes */}
        <span style={{ display:"flex", alignItems:"center", gap:6 }}>
          <Icon name="calendar3" size={13} color="var(--text3)"/>
          <button aria-label="Mes anterior" className="btn" title="Mes anterior" onClick={() => mover(-1)} style={navS}>
            <Icon name="chevron-left" size={11} color="var(--text2)"/>
          </button>
          <span style={{ fontWeight:800, fontSize:15, minWidth:132, textAlign:"center" }}>
            {MESES[mes]} {anio}
          </span>
          {/* Al mes que viene no hay nada que ir a ver: esta vacio por definicion. */}
          <button aria-label={mesFuturo ? "No hay meses por delante" : "Mes siguiente"} className="btn" title={mesFuturo ? "No hay meses por delante" : "Mes siguiente"}
            onClick={() => mover(1)} disabled={mesFuturo}
            style={{ ...navS, cursor: mesFuturo ? "not-allowed" : "pointer", opacity: mesFuturo ? .4 : 1 }}>
            <Icon name="chevron-right" size={11} color="var(--text2)"/>
          </button>
        </span>

        <span style={sepS}/>

        {/* La semana. Es la que se detalla arriba en "Mi jornada" y la que queda
            remarcada abajo en el calendario: un solo control para las dos. */}
        <span style={{ display:"flex", alignItems:"center", gap:6 }}>
          <Icon name="calendar-week" size={13} color="var(--accent)"/>
          <button aria-label="Semana anterior" className="btn" title="Semana anterior" onClick={() => moverSemana(-1)} style={navS}>
            <Icon name="chevron-left" size={11} color="var(--text2)"/>
          </button>
          <span title={`Semana ${numeroSemana(semana)} del año · se detalla arriba y va remarcada en el calendario`}
            style={{ fontSize:12, fontWeight:700, color:"var(--accent)", minWidth:86,
                     textAlign:"center", whiteSpace:"nowrap" }}>
            {rangoSemana}
          </span>
          <button aria-label={semanaFutura ? "No hay semanas por delante" : "Semana siguiente"} className="btn" title={semanaFutura ? "No hay semanas por delante" : "Semana siguiente"}
            onClick={() => moverSemana(1)} disabled={semanaFutura}
            style={{ ...navS, cursor: semanaFutura ? "not-allowed" : "pointer",
                     opacity: semanaFutura ? .4 : 1 }}>
            <Icon name="chevron-right" size={11} color="var(--text2)"/>
          </button>
        </span>

        {/* Volver a hoy en un gesto: mes y semana a la vez, que es justo lo que
            antes obligaba a cuadrar dos controles a mano. */}
        {!(esMesActual && esSemanaActual) && (
          <button className="btn" onClick={() => { irAlMes(hoy.getFullYear(), hoy.getMonth());
                                                   setSemana(lunesDe(isoDeHoy)); }}
            title="Volver al mes y la semana de hoy"
            style={{ ...navS, padding:"5px 10px", fontSize:11, fontWeight:600,
                     fontFamily:"var(--font)", color:"var(--text2)" }}>
            Hoy
          </button>
        )}

        {/* Filtro por equipos. Va en un desplegable y no en una barra lateral:
            el calendario del mes ya ocupa el ancho y la ficha del dia se abre
            a su derecha. */}
        {verOtros && nEquipos > 0 && (
          <div style={{ position:"relative", marginLeft:8 }}>
            <button ref={desplSel.boton} className="btn" onClick={() => setAbiertoSel(v => !v)}
              aria-expanded={abiertoSel} aria-haspopup="dialog"
              style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 10px",
                       borderRadius:9, border:"1px solid var(--border)",
                       background: nMarcados < nEquipos ? "var(--accent-bg)" : "var(--surface)",
                       color: nMarcados < nEquipos ? "var(--accent)" : "var(--text2)",
                       fontSize:11.5, fontWeight:600, cursor:"pointer" }}>
              <Icon name="people-fill" size={11}
                color={nMarcados < nEquipos ? "var(--accent)" : "var(--text2)"}/>
              {nMarcados === nEquipos ? "Todos los equipos"
                : nMarcados === 0     ? "Ningún equipo"
                : soloComparar        ? `Comparando ${nMarcados} de ${nEquipos} equipos`
                : `${nMarcados} de ${nEquipos} equipos`}
              <Icon name="chevron-down" size={9}
                color={nMarcados < nEquipos ? "var(--accent)" : "var(--text3)"}/>
            </button>

            {abiertoSel && (
              <>
                {/* Capa para cerrar pulsando fuera */}
                <div onClick={() => setAbiertoSel(false)} aria-hidden="true"
                  style={{ position:"fixed", inset:0, zIndex:40 }}/>
                <div ref={desplSel.panel} role="dialog" aria-label={soloComparar ? "Comparar equipos" : "Equipos"}
                  style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:41,
                              width:250, maxWidth:"calc(100vw - 24px)", background:"var(--surface)", borderRadius:11,
                              border:"1px solid var(--border)", boxShadow:"var(--shadow-md)",
                              padding:"11px 12px" }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:8 }}>
                    <span style={{ flex:1, fontSize:11, fontWeight:700, color:"var(--text3)",
                                   textTransform:"uppercase", letterSpacing:".1em" }}>
                      {soloComparar ? "Comparar equipos" : "Equipos"}
                    </span>
                    {/* Con la regla de comparacion, "todos" y "ninguno" son
                        justo los dos extremos que no se permiten: el atajo que
                        queda es volver a lo mio. */}
                    {soloComparar
                      ? (misEquipos.length > 0 && (
                          <button className="btn" onClick={soloLosMios} style={linkS}>solo el mío</button>))
                      : (<>
                          <button className="btn" onClick={todosLosEquipos} style={linkS}>todos</button>
                          <span style={{ color:"var(--border)" }}>·</span>
                          <button className="btn" onClick={ningunEquipo} style={linkS}>ninguno</button>
                        </>)}
                  </div>

                  {soloComparar && (
                    <div style={{ fontSize:11, color:"var(--text3)", lineHeight:1.45,
                                  marginBottom:7 }}>
                      Marca los departamentos que quieras comparar.
                      {niTodos && " La plantilla entera de una vez no: el promedio de todos los "
                                + "equipos no dice nada de ninguno."}
                    </div>
                  )}

                  <div style={{ maxHeight:260, overflowY:"auto", margin:"0 -2px", padding:"0 2px" }}>
                    {teams.map(t => {
                      const key = String(t.id);
                      const on  = !equiposOn || equiposOn.has(key);
                      const mio = (t.members || []).map(String).includes(String(currentUser?.id));
                      // El motivo va en el title de la fila y no en un aviso
                      // aparte: la casilla que no responde se explica donde se
                      // intenta pulsar.
                      const bloqueo = bloqueoDe(key);
                      return (
                        <label key={key} title={bloqueo || t.name}
                          style={{ ...filaChkS, background: mio ? "var(--accent-bg)" : "transparent",
                                   borderRadius:6, padding:"4px 6px", margin:"0 -6px",
                                   cursor: bloqueo ? "not-allowed" : "pointer",
                                   opacity: bloqueo ? .55 : 1 }}>
                          <input type="checkbox" checked={on} disabled={!!bloqueo}
                            onChange={() => toggleEquipo(key)}
                            style={{ accentColor: t.color || "var(--accent)", width:14, height:14,
                                     cursor: bloqueo ? "not-allowed" : "pointer", flexShrink:0 }}/>
                          <span style={{ flex:1, fontWeight:on ? 600 : 400, whiteSpace:"nowrap",
                                         overflow:"hidden", textOverflow:"ellipsis" }}>{t.name}</span>
                          {mio && <Icon name="person-check-fill" size={10} color="var(--accent)"/>}
                          <span style={{ fontSize:11, color:"var(--text3)", flexShrink:0 }}>
                            {(t.members || []).length}
                          </span>
                        </label>
                      );
                    })}

                    {sinEquipo.size > 0 && (
                      <label title={bloqueoDe(SIN_EQUIPO) || "Quien no está en ningún equipo"}
                        style={{ ...filaChkS, padding:"4px 6px", margin:"0 -6px",
                                 cursor: bloqueoDe(SIN_EQUIPO) ? "not-allowed" : "pointer",
                                 opacity: bloqueoDe(SIN_EQUIPO) ? .55 : 1 }}>
                        <input type="checkbox" disabled={!!bloqueoDe(SIN_EQUIPO)}
                          checked={!equiposOn || equiposOn.has(SIN_EQUIPO)}
                          onChange={() => toggleEquipo(SIN_EQUIPO)}
                          style={{ accentColor:"var(--text3)", width:14, height:14,
                                   cursor: bloqueoDe(SIN_EQUIPO) ? "not-allowed" : "pointer",
                                   flexShrink:0 }}/>
                        <span style={{ flex:1 }}>Sin equipo</span>
                        <span style={{ fontSize:11, color:"var(--text3)" }}>{sinEquipo.size}</span>
                      </label>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Y dentro de los equipos marcados, quien. El filtro de equipos elige
            departamentos; este elige personas, que es el contraste fino: dos
            compañeros del mismo equipo, o uno de cada uno. Va al lado y no
            dentro del de equipos porque son dos preguntas distintas y se
            cambian por separado. */}
        {verOtros && gente.length > 0 && (
          <div style={{ position:"relative" }}>
            <button ref={desplPers.boton} className="btn" onClick={() => setAbiertoPers(v => !v)}
              aria-expanded={abiertoPers} aria-haspopup="dialog"
              title="Quién sale con su barra en Mi jornada"
              style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 10px",
                       borderRadius:9, border:"1px solid var(--border)",
                       background: personasOff.size ? "var(--accent-bg)" : "var(--surface)",
                       color: personasOff.size ? "var(--accent)" : "var(--text2)",
                       fontSize:11.5, fontWeight:600, cursor:"pointer" }}>
              <Icon name="person-check-fill" size={11}
                color={personasOff.size ? "var(--accent)" : "var(--text2)"}/>
              {genteOn.length === gente.length
                ? `${gente.length} persona${gente.length === 1 ? "" : "s"}`
                : `${genteOn.length} de ${gente.length} personas`}
              <Icon name="chevron-down" size={9}
                color={personasOff.size ? "var(--accent)" : "var(--text3)"}/>
            </button>

            {abiertoPers && (
              <>
                <div onClick={() => setAbiertoPers(false)} aria-hidden="true"
                  style={{ position:"fixed", inset:0, zIndex:40 }}/>
                <div ref={desplPers.panel} role="dialog" aria-label="Comparar personas"
                  style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:41,
                              width:262, maxWidth:"calc(100vw - 24px)", background:"var(--surface)",
                              borderRadius:11, border:"1px solid var(--border)",
                              boxShadow:"var(--shadow-md)", padding:"11px 12px" }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:8 }}>
                    <span style={{ flex:1, fontSize:11, fontWeight:700, color:"var(--text3)",
                                   textTransform:"uppercase", letterSpacing:".1em" }}>
                      Comparar personas
                    </span>
                    <button className="btn" onClick={() => setPersonasOff(new Set())}
                      style={linkS}>todas</button>
                    <span style={{ color:"var(--border)" }}>·</span>
                    <button className="btn" style={linkS}
                      onClick={() => setPersonasOff(new Set(gente.map(p => p.uid)))}>ninguna</button>
                  </div>

                  {/* Doce es lo que aguanta la tira de barras de un dia. Pasado
                      eso no se prohibe nada —puede que quieras verlo— pero se
                      dice, porque la culpa de que no se lea parece del grafico. */}
                  {genteOn.length > 12 && (
                    <div style={{ fontSize:11, color:"#f59e0b", lineHeight:1.45,
                                  marginBottom:7 }}>
                      {genteOn.length} barras por día se aprietan mucho: desmarca a quien no
                      estés comparando y las fotos volverán al pie de cada barra.
                    </div>
                  )}

                  <div style={{ maxHeight:280, overflowY:"auto", margin:"0 -2px", padding:"0 2px" }}>
                    {gente.map(p => {
                      const on = !personasOff.has(p.uid);
                      return (
                        <label key={p.uid} title={p.equipo || "Sin equipo"}
                          style={{ ...filaChkS, borderRadius:6, padding:"3px 6px", margin:"0 -6px" }}>
                          <input type="checkbox" checked={on} onChange={() => togglePersona(p.uid)}
                            style={{ accentColor:p.color, width:14, height:14,
                                     cursor:"pointer", flexShrink:0 }}/>
                          <AvatarOdoo modelo="hr.employee" id={p.empId} nombre={p.nombre} size={19}/>
                          <span style={{ flex:1, fontWeight:on ? 600 : 400, whiteSpace:"nowrap",
                                         overflow:"hidden", textOverflow:"ellipsis" }}>
                            {p.nombre}
                          </span>
                          {/* El color con el que sale su barra arriba */}
                          <i style={{ width:9, height:9, borderRadius:2, background:p.color,
                                      opacity: on ? 1 : .3, flexShrink:0 }}/>
                        </label>
                      );
                    })}
                  </div>

                  <div style={{ fontSize:11, color:"var(--text3)", lineHeight:1.45,
                                marginTop:8, borderTop:"1px solid var(--border2)", paddingTop:7 }}>
                    Solo sale quien tenga algún fichaje o una ausencia aprobada en el mes.
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Traer de Odoo lo que falte. Es de quien pulsa y de nadie mas: se
            consulta con su propia sesion de Odoo. */}
        <span style={{ display:"flex", alignItems:"stretch", marginLeft:8, borderRadius:9,
                       border:"1px solid var(--border)", background:"var(--surface)", overflow:"hidden" }}>
          <button className="btn" onClick={sincronizar} disabled={sincronizando}
            title={"Trae al registro TUS fichajes que estén en Odoo y aquí falten.\n"
                   + "De los demás no se puede: cada empleado se consulta con su propia sesión de Odoo."}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 10px",
                     border:"none", background:"transparent",
                     color:"var(--text2)", fontSize:11.5, fontWeight:600,
                     cursor: sincronizando ? "wait" : "pointer", opacity: sincronizando ? .6 : 1 }}>
            <Icon name="arrow-repeat" size={11} color="var(--text2)"/>
            {sincronizando ? "Contrastando…" : "Sincronizar con Odoo"}
          </button>
          <select value={dias} onChange={e => setDias(Number(e.target.value))}
            title="Cuánto historial se le pide a Odoo"
            style={{ padding:"6px 6px", border:"none", borderLeft:"1px solid var(--border)",
                     background:"transparent", color:"var(--text2)", fontSize:11,
                     fontFamily:"var(--font)", cursor:"pointer" }}>
            <option value={60}>60 días</option>
            <option value={180}>6 meses</option>
            <option value={365}>1 año</option>
          </select>
        </span>

        {/* Fichar sin salir de aqui: el estado (dentro o fuera) y el boton, con confirmacion. */}
        <span style={{ marginLeft:"auto" }}><FicharAhora fichaje={fichaje}/></span>

        <span style={{ fontSize:11.5, color:"var(--text3)" }}>
          {cargando ? "Cargando…"
            : `${filas.length} fichaje${filas.length === 1 ? "" : "s"} en el mes` +
              (filas.length !== todas.length ? ` · ${todas.length} en total` : "")}
        </span>
      </div>

      {/* Mis horas de la semana elegida arriba, con la media de los equipos
          marcados al lado de cada barra. El calendario de abajo es de la
          plantilla y se mira para otra cosa. */}
      <HorasSemanales showToast={showToast} anio={anio} mes={mes} lunesIso={semana}
        onSemana={setSemana} onMes={irAlMes} comparativa={comparativa}
        miEmpleado={empleadoDe[String(currentUser?.id)] || null}
        miJornada={jornadaDe(currentUser?.id)}/>

      {/* Con un dia abierto, el detalle va al lado y no debajo: apilados, la
          ficha caia fuera de pantalla y habia que bajar a leerla. */}
      <div className="grid-1col-mob aside-380" style={{ display:"grid", gap:12,
                    gridTemplateColumns: diaAbierto ? "minmax(0,1fr) 380px" : "1fr",
                    alignItems:"start", minHeight:0 }}>
      {/* Calendario y resumen van EN LA MISMA COLUMNA. Estando el resumen
          fuera de esta rejilla se estiraba a todo el ancho —tabla de seis
          columnas de punta a punta de la pantalla— mientras el calendario se
          quedaba estrecho por la ficha del dia, y encima dejaba un hueco
          enorme entre los dos cuando la ficha era alta. */}
      <div style={{ display:"flex", flexDirection:"column", gap:12, minWidth:0 }}>
      <div style={{ ...card, padding:12 }}>
        {/* Una columna mas a la izquierda: la semana. La que se esta mirando
            arriba queda remarcada de punta a punta, y las demas son botones
            para cambiarla sin salir del calendario. Es el enganche que faltaba
            entre las dos mitades de la pantalla. */}
        <div style={{ display:"grid", gridTemplateColumns:"34px repeat(7,minmax(0,1fr))", gap:6 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"var(--text3)",
                        textAlign:"center", paddingBottom:4 }}>Sem</div>
          {DIAS.map(d => (
            <div key={d} style={{ fontSize:11, fontWeight:700, color:"var(--text3)",
                                  textAlign:"center", paddingBottom:4 }}>{d}</div>
          ))}

          {semanasRejilla.map(({ fila, lunes }) => {
            const sel = lunes === semana;
            return (
            <Fragment key={lunes}>
              {/* aria-current, y no solo el color: es la semana "actual" de la
                  pantalla, y asi un lector de pantalla dice cual esta puesta. */}
              <button className="btn" onClick={() => setSemana(lunes)}
                aria-current={sel ? "true" : undefined}
                title={sel ? `Semana ${numeroSemana(lunes)}: es la que se detalla arriba en Mi jornada`
                           : `Ver la semana ${numeroSemana(lunes)} (${Number(lunes.slice(8))}`
                             + `–${Number(sumarDias(lunes, 6).slice(8))}) arriba, en Mi jornada`}
                style={{ borderRadius:9, border:`1px solid ${sel ? "var(--accent)" : "var(--border2)"}`,
                         background: sel ? "var(--accent)" : "var(--surface2)",
                         color: sel ? "#fff" : "var(--text3)", cursor:"pointer",
                         fontFamily:"var(--font)", fontSize:11, fontWeight:800,
                         display:"flex", alignItems:"center", justifyContent:"center" }}>
                {numeroSemana(lunes)}
              </button>

              {fila.map((iso, i) => {
                if (!iso) return <div key={i} style={{ borderRadius:9,
                        background: sel ? "var(--accent-bg)" : "transparent" }}/>;
                const delDia = porDia[iso] || [];
                const personas = [...new Set(delDia.map(f => f.user_id))];
                // Cada tramo cuenta una vez: la entrada y la salida llevan las
                // mismas horas trabajadas, y sumarlas doblaba la jornada del dia.
                const horas = horasReales(delDia);
                const fallos = delDia.filter(f => f.odoo_estado !== "ok").length;

                // El dia cuya ficha esta abierta a la derecha. Con la semana
                // entera teñida en el mismo azul, la celda pulsada se perdia
                // entre sus seis vecinas y no habia forma de saber de que dia
                // era la lista de horarios de al lado.
                const abierto = iso === diaAbierto;

                return (
                  <div key={iso} {...clicable(() => setDiaAbierto(iso), { activo: delDia.length > 0 })}
                    aria-current={abierto ? "true" : undefined}
                    style={{ minHeight:78, borderRadius:9, padding:"6px 7px",
                             border: abierto ? "2px solid var(--accent)"
                                   : iso === isoDeHoy ? "1.5px solid var(--accent)"
                                   : "1px solid var(--border2)",
                             // La semana elegida se tiñe entera, con fichajes o sin
                             // ellos: si solo se marcaran los dias con datos, una
                             // semana floja no se distinguiria de otra sin marcar.
                             background: abierto ? "var(--accent-bg)"
                                       : delDia.length ? "var(--surface2)"
                                       : sel ? "var(--accent-bg)" : "transparent",
                             // Levantado del plano: el resalte de la semana es
                             // un contorno interior, y otro contorno encima no
                             // se distinguiria del de al lado.
                             boxShadow: abierto ? "var(--shadow-md)"
                                      : sel ? "inset 0 0 0 1.5px var(--accent)" : "none",
                             cursor: delDia.length ? "pointer" : "default",
                             display:"flex", flexDirection:"column", gap:3 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      {/* El numero en pastilla llena: es la marca que se lee de
                          un vistazo desde la ficha de la derecha. */}
                      <span style={{ fontSize:11,
                                     fontWeight: abierto || iso === isoDeHoy ? 800 : 600,
                                     color: abierto ? "#fff" : "var(--text2)",
                                     background: abierto ? "var(--accent)" : "transparent",
                                     borderRadius: abierto ? 6 : 0,
                                     padding: abierto ? "1px 6px" : 0,
                                     margin: abierto ? "-1px 0" : 0 }}>
                        {Number(iso.slice(8))}
                      </span>
                      {fallos > 0 && (
                        <Icon name="exclamation-triangle-fill" size={9} color="#f59e0b"
                          style={{ marginLeft:"auto" }}/>
                      )}
                    </div>

                    {delDia.length > 0 && (
                      <>
                        <div style={{ display:"flex", gap:2, flexWrap:"wrap" }}>
                          {personas.slice(0, 4).map(uid => {
                            const f = delDia.find(x => x.user_id === uid);
                            return <Avatar key={uid} name={f.user_name} size={16}/>;
                          })}
                          {personas.length > 4 && (
                            <span style={{ fontSize:11, color:"var(--text3)", alignSelf:"center" }}>
                              +{personas.length - 4}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:11, color:"var(--text3)", marginTop:"auto" }}>
                          {delDia.length} fichaje{delDia.length === 1 ? "" : "s"}
                          {horas > 0 && ` · ${fmtHoras(horas)}`}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </Fragment>
            );
          })}
        </div>
      </div>

        {/* La cuenta del mes de la gente que se esta viendo. Recibe las filas ya
            filtradas por equipo, asi que enseña lo mismo que el calendario, y de
            que equipo es cada uno para poder compararlos entre si. */}
        <ResumenEquipo filas={filas} users={users} anio={anio} mes={mes}
          festivos={festivos} vacaciones={vacaciones} equipoDe={nombreEquipo}
          jornadaDe={jornadaDe}/>
      </div>

        {diaAbierto && (
          <DetalleDia iso={diaAbierto} filas={detalle} nombreEquipo={nombreEquipo}
            onBorrar={borrarFallido}
            puedeBorrar={f => puedeTocarAsistencia(currentUser, teams, f.user_id)}
            onClose={() => setDiaAbierto(null)}/>
        )}
      </div>

    </div>
  );
}

// ── Detalle de un día ─────────────────────────────────────────────────────────

function DetalleDia({ iso, filas, nombreEquipo, onBorrar, puedeBorrar, onClose }) {
  const card = { background:"var(--surface)", borderRadius:13, border:"1px solid var(--border)",
                 boxShadow:"var(--shadow-sm)" };
  // Agrupado por persona: en un día con varias personas, la lista plana obliga a
  // ir saltando entre nombres para seguir la jornada de cada una.
  const porPersona = {};
  filas.forEach(f => { (porPersona[f.user_id] = porPersona[f.user_id] || []).push(f); });

  // Un tramo sin salida es cosa distinta segun el dia: hoy es una jornada en
  // marcha, y en un dia pasado es un fichaje que nadie cerro.
  const esHoy = iso === new Intl.DateTimeFormat("sv-SE", { timeZone:"Europe/Madrid" }).format(new Date());

  // En pantalla estrecha el detalle cae debajo del calendario y no se ve que se
  // haya abierto nada. Al cambiar de dia se trae a la vista.
  const caja = useRef(null);
  useEffect(() => { caja.current?.scrollIntoView({ behavior:"smooth", block:"nearest" }); }, [iso]);

  return (
    // Columna propia con su scroll: el calendario es alto y, sin esto, la ficha
    // del dia se quedaba a medias contra el final de la ventana.
    <div ref={caja} style={{ ...card, padding:16, position:"sticky", top:8,
                             maxHeight:"calc(100vh - 130px)", overflowY:"auto" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <span style={{ fontWeight:800, fontSize:14 }}>
          {new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES",
            { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
        </span>
        <button aria-label="Cerrar" className="btn" onClick={onClose}
          style={{ marginLeft:"auto", padding:6, borderRadius:8, border:"1px solid var(--border)",
                   background:"var(--surface2)", cursor:"pointer" }}>
          <Icon name="x-lg" size={12} color="var(--text3)"/>
        </button>
      </div>

      {Object.entries(porPersona).map(([uid, lista]) => (
        <div key={uid} style={{ marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6 }}>
            <Avatar name={lista[0].user_name} size={22}/>
            <span style={{ fontSize:12.5, fontWeight:700 }}>{lista[0].user_name}</span>
            {(() => {
              const eq = nombreEquipo?.(uid);
              return eq ? (
                <span style={{ fontSize:11, fontWeight:700, padding:"1px 6px", borderRadius:20,
                               border:`1px solid ${eq.color}`, color:eq.color }}>{eq.name}</span>
              ) : null;
            })()}
            <span style={{ fontSize:11, color:"var(--text3)" }}>
              · {fmtHoras(horasReales(lista))}
            </span>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            {tramosDe(lista).map(t => (
              <Tramo key={t.clave} tramo={t} enCurso={esHoy}
                onBorrar={onBorrar} puedeBorrar={puedeBorrar}/>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Tramos ────────────────────────────────────────────────────────────────────

// Un tramo de Odoo llega partido en dos filas —la entrada y la salida comparten
// odoo_attendance_id— y en pantalla se leian como dos fichajes sin relacion, con
// la misma hora trabajada repetida en las dos. Se juntan para que cada linea sea
// una jornada de verdad: 08:15 → 10:54.
//
// Lo que no tiene id de asistencia no se agrupa. Son los intentos que no
// llegaron a Odoo, y ahi cada fila si es un fichaje suelto: juntarlos por
// cercania inventaria tramos que nunca existieron.
function tramosDe(lista) {
  const porTramo = new Map();
  const sueltos = [];

  for (const f of lista) {
    if (!f.odoo_attendance_id) {
      sueltos.push({ clave:`f${f.id}`, suelto:true,
                     entrada: f.accion === "entrada" ? f : null,
                     salida:  f.accion === "salida"  ? f : null });
      continue;
    }
    const clave = `a${f.odoo_attendance_id}`;
    const t = porTramo.get(clave) || { clave, att:f.odoo_attendance_id, entrada:null, salida:null };
    if (f.accion === "salida") t.salida = f; else t.entrada = f;
    porTramo.set(clave, t);
  }

  // Por hora de inicio: los fichajes llegan ordenados, pero al agrupar un tramo
  // se coloca donde cayo su primera fila y un suelto podria adelantarlo.
  const inicio = t => Date.parse((t.entrada || t.salida).ts_servidor) || 0;
  return [...porTramo.values(), ...sueltos].sort((a, b) => inicio(a) - inicio(b));
}

// La hora que vale es la que tiene Odoo, no la del boton: cuando se aprueba una
// modificacion de asistencia, ALM guarda la nueva en odoo_check_in /
// odoo_check_out y ts_servidor se queda con el momento real en que se pulso, que
// es el registro legal. Se pinta la de Odoo y se marca si ya no coinciden.
//
// El minuto de margen es porque las dos horas nunca son identicas al segundo: el
// boton de ALM y el registro de Odoo se escriben con decimas de diferencia, y sin
// margen todos los fichajes saldrian marcados como corregidos.
function horaDe(f, campo) {
  if (!f) return null;
  const odooTs = f[campo];
  // El extremo que le toca a esta fila: la entrada compara con odoo_check_in y
  // la salida con odoo_check_out. Cada fila guarda copia de las dos horas del
  // tramo, asi que la de la fila contraria sirve para pintar pero no para decir
  // si esta corregida: son horas distintas por definicion.
  const suya = (f.accion === "entrada") === (campo === "odoo_check_in");
  if (!odooTs) return suya ? { ts: f.ts_servidor, corregida: false } : null;
  return { ts: odooTs, corregida: suya && Math.abs(Date.parse(odooTs) - Date.parse(f.ts_servidor)) > 60000 };
}

// Una linea por tramo, con el detalle plegado: la IP y el punto de GPS de cada
// extremo son cuatro datos por fichaje y desplegados dejaban la ficha del dia
// en una lista imposible de recorrer. Se abre el tramo que interesa y ya.
function Tramo({ tramo, enCurso, onBorrar, puedeBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const { entrada, salida } = tramo;

  // Las horas salen de la misma fila que la hora de salida que se pinta (ver
  // horasTramo.js): con el maximo de las dos, una correccion a la baja en Odoo
  // se quedaba con la cifra vieja si una de las filas no se habia refrescado.
  const horas = horasDeTramo(entrada, salida) || 0;
  const fallo = [entrada, salida].find(f => f && f.odoo_estado !== "ok");
  const trabajando = entrada && !salida && enCurso;
  const color = fallo ? "#f59e0b" : salida ? "var(--green)" : trabajando ? "var(--accent)" : "var(--red)";

  const hora = { fontSize:12, fontVariantNumeric:"tabular-nums", fontWeight:600 };

  const eIn  = horaDe(entrada, "odoo_check_in")  || horaDe(salida,  "odoo_check_in");
  const eOut = horaDe(salida,  "odoo_check_out") || horaDe(entrada, "odoo_check_out");

  return (
    <div style={{ borderRadius:8, background:"var(--surface2)", border:`1px solid color-mix(in srgb, ${color} 45%, transparent)` }}>
      <div onClick={() => setAbierto(v => !v)}
        style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap",
                 padding:"7px 10px", cursor:"pointer" }}>
        <button type="button" className="ct-toggle" aria-expanded={abierto} aria-label="Detalle del tramo"
          style={{ background:"none", border:0, padding:0, display:"inline-flex", cursor:"pointer" }}>
          <Icon name={abierto ? "chevron-down" : "chevron-right"} size={9} color="var(--text3)"/>
        </button>

        <span style={hora} title={eIn?.corregida ? `Pulsado a las ${hhmm(entrada.ts_servidor, "—")}; en Odoo consta ${hhmm(eIn.ts, "—")}` : ""}>
          {eIn ? hhmm(eIn.ts, "—") : "—"}
          {eIn?.corregida && <Icon name="pencil" size={8} color="var(--accent)" style={{ marginLeft:3 }}/>}
        </span>
        <span style={{ color:"var(--text3)", fontSize:11 }}>→</span>
        <span style={{ ...hora, color: salida ? "var(--text)" : "var(--text3)" }}
          title={eOut?.corregida ? `Pulsado a las ${hhmm(salida.ts_servidor, "—")}; en Odoo consta ${hhmm(eOut.ts, "—")}` : ""}>
          {eOut ? hhmm(eOut.ts, "—") : trabajando ? "en curso" : "sin cerrar"}
          {eOut?.corregida && <Icon name="pencil" size={8} color="var(--accent)" style={{ marginLeft:3 }}/>}
        </span>

        {horas > 0 && (
          <span style={{ fontSize:11, color:"var(--text3)" }}>{fmtHoras(horas)}</span>
        )}

        <span style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:5 }}>
          {!fallo
            ? <><Icon name="check-circle-fill" size={10} color="var(--ok-text)"/>
                <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase",
                               letterSpacing:".07em", color:"var(--text3)" }}>
                  Odoo #{tramo.att}
                </span></>
            : <><Icon name="exclamation-triangle-fill" size={10} color="#f59e0b"/>
                <span style={{ fontSize:11, color:"#f59e0b" }} title={fallo.odoo_error || ""}>
                  {fallo.odoo_estado === "pendiente" ? "Sin sincronizar" : "Error en Odoo"}
                </span>
                {/* Solo en los que fallaron: un intento que no llego a Odoo no es
                    jornada, y hasta ahora se quedaba en el registro ensuciando
                    el dia. Y solo a quien puede corregir a esa persona. */}
                {puedeBorrar?.(fallo) && (
                  <button aria-label="Descartar del registro" className="btn" title="Descartar del registro"
                    onClick={e => { e.stopPropagation(); onBorrar?.(fallo); }}
                    style={{ padding:3, borderRadius:6, border:"1px solid var(--border)",
                             background:"var(--surface)", cursor:"pointer", lineHeight:0 }}>
                    <Icon name="trash3" size={10} color="var(--red-text)"/>
                  </button>)}</>}
        </span>
      </div>

      {abierto && (
        <div style={{ padding:"0 10px 8px 24px", display:"flex", flexDirection:"column", gap:4 }}>
          {entrada && <Extremo f={entrada} etiqueta="Entrada" color="var(--ok-text)"/>}
          {salida  && <Extremo f={salida}  etiqueta="Salida"  color="var(--red-text)"/>}
        </div>
      )}
    </div>
  );
}

// De donde se ficho cada extremo del tramo.
function Extremo({ f, etiqueta, color }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:9, flexWrap:"wrap" }}>
      <span style={{ fontSize:11, fontWeight:700, width:52, textTransform:"uppercase",
                     letterSpacing:".06em", color }}>{etiqueta}</span>

      {/* La IP del empleado es la de ALM; la de Odoo es la del servidor y por eso
          no se muestra aquí. De las filas traídas de Odoo no hay IP que enseñar:
          no se pulsaron aquí. */}
      {f.origen === "odoo" ? (
        <span title="Este fichaje no se hizo desde ALM: se ha traído de Odoo al sincronizar"
          style={{ fontSize:11, fontWeight:700, padding:"1px 6px", borderRadius:20,
                   background:"var(--surface)", color:"var(--text3)" }}>
          DE ODOO
        </span>
      ) : (
        <span style={{ fontSize:11, color:"var(--text3)", fontFamily:"var(--mono)" }}>
          {f.ip || "—"}
        </span>
      )}

      {f.latitud !== null && f.longitud !== null ? (
        <a href={`https://www.google.com/maps?q=${f.latitud},${f.longitud}`}
          target="_blank" rel="noreferrer" title={precisionTexto(f.precision_m)}
          style={{ fontSize:11, color:"var(--accent)", display:"flex",
                   alignItems:"center", gap:4, textDecoration:"none" }}>
          <Icon name="globe" size={10} color="var(--accent)"/>
          {f.latitud.toFixed(4)}, {f.longitud.toFixed(4)}
          {/* El radio del punto: un fichaje desde el movil cae con metros y uno
              desde un PC sin GPS, con kilometros. Sin esto los dos se leian
              igual. */}
          {f.precision_m > 0 && (
            <span style={{ color: colorPrecision(f.precision_m), fontWeight:600 }}>
              ±{f.precision_m >= 1000 ? `${(f.precision_m / 1000).toFixed(1)} km` : `${f.precision_m} m`}
            </span>
          )}
        </a>
      ) : (
        <span style={{ fontSize:11, color:"var(--text3)" }}>sin ubicación</span>
      )}

      {sitio(f) && <span style={{ fontSize:11, color:"var(--text3)" }}>{sitio(f)}</span>}
    </div>
  );
}

const navS = { padding:"5px 8px", borderRadius:8, border:"1px solid var(--border)",
               background:"var(--surface2)", cursor:"pointer", lineHeight:0 };
const sepS = { width:1, alignSelf:"stretch", background:"var(--border2)", margin:"0 2px" };

const filaChkS = { display:"flex", alignItems:"center", gap:7, cursor:"pointer",
                   padding:"4px 0", fontSize:12, color:"var(--text2)" };
const linkS    = { background:"none", border:"none", padding:0, cursor:"pointer", fontSize:11,
                   color:"var(--accent)", fontFamily:"var(--font)", fontWeight:600, minHeight:24, minWidth:24 };
